import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {map, retry, share, shareReplay, startWith, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {EventSourceService} from './event-source.service';

interface FavoriteType { [s: string]: boolean; }
@Injectable({
  providedIn: 'root'
})
export class DataService {
  private favorites$: BehaviorSubject<FavoriteType>;

  public readonly unfavParkingData$: Observable<ParkingDeck[]>;
  public readonly favParkingData$: Observable<ParkingDeck[]>;

  constructor(private http: HttpClient, private es: EventSourceService) {
    const hasLocalStorage = 'localStorage' in window;
    // initialize favorite decks. Load them from local storage if it's supported
    let initFavs = {};
    let initDecks: ParkingDeck[] = [];

    if (hasLocalStorage) {
      const stored = window.localStorage.getItem('favorites');

      if (stored) {
        initFavs = JSON.parse(stored);
      }

      const storedDecks = window.localStorage.getItem('decks');
      if (storedDecks) {
        initDecks = JSON.parse(storedDecks);
        initDecks.forEach(d => d.percentAvailable = null);
      }
    }

    let parkingStream$ = this.es.parkingDeckData$.pipe(startWith(initDecks));

    // create a behavior subject for favorite decks
    this.favorites$ = new BehaviorSubject<FavoriteType>(initFavs);

    // if local storage is supported, subscribe to changes and store favorites
    if (hasLocalStorage) {
      this.favorites$.subscribe(newF => window.localStorage.setItem('favorites', JSON.stringify(newF)));
      parkingStream$ = parkingStream$.pipe(tap(decks => window.localStorage.setItem('decks', JSON.stringify(decks))));
    }

    // create a combined observable of favorites and latest deck data. This will be used to update the favorite
    // flag on each deck, and will be split into two observables for favorite and unselected decks
    const combined = combineLatest(this.favorites$, parkingStream$).pipe(
      tap(([favs, decks]) => decks.filter(deck => deck.lotCode in favs).forEach(deck => deck.favorite = true)),
      shareReplay(1),
      retry()
    );

    // create a favorite decks observable by filtering decks using the stored favorites list, and sorting it in the same order
    this.favParkingData$ = combined.pipe(
      map(([favs, decks]) => {
          const filteredDecks: ParkingDeck[] = decks.filter(d => d.lotCode in favs);
          return Object.keys(favs).map(key => filteredDecks.find(deck => deck.lotCode === key));
        }
      )
    );

    // create an unselected decks observable by filter decks to the ones not in the favorites list, then sort alphabetically
    this.unfavParkingData$ = combined.pipe(
      map(([favs, decks]) => decks.filter((d: ParkingDeck) => !(d.lotCode in favs))),
      tap(decks => decks.sort((a, b) => {
        const al = a.name.toLowerCase();
        const bl = b.name.toLowerCase();
        return al === bl ? 0 : (al < bl ? -1 : 1);
      }))
    );
  }

  // add a deck to the favorites hash map, then signal the favorites observable to update the stream
  public saveFavorite(deck: ParkingDeck) {
    const f = this.favorites$.getValue();
    f[deck.lotCode] = true;
    this.favorites$.next(f);
  }

  // remove the deck from the favorites hash map, then signal the favorites observable to update the stream
  public removeFavorite(deck: ParkingDeck) {
    const f = this.favorites$.getValue();
    delete f[deck.lotCode];

    this.favorites$.next(f);
  }

  public get isLive() {
    return this.es.isLive$;
  }
}

// a parking deck interface
export interface ParkingDeck {
  lotCode: string;
  name: string;
  percentAvailable: number;
  style: string;
  mapLink: string;
  favorite: boolean;
}
